import React, { useEffect, useState, useRef, useCallback } from "react";
import { Row, Col, Button } from "react-bootstrap";
import ReactPaginate from "react-paginate";
import { useNavigate } from "react-router-dom";
import { Toast, ToastContainer } from "react-bootstrap";
import { apiCall } from "../../components/service/api-service";

//Context & Permissions
import { useGlobal } from "../../GlobalContext";
import { canCreate, canEdit, canDelete, canView } from "../common/permissionUtils";

//API Imports
import { getContactsListApi, bulkDeleteContactsApi } from "./contacts-api";
import SearchWithFreeTextAPI from "../common/searchbox-free-text.js";

//SVG Images
import Search from "../../assets/svg/search.svg";
import Filter from "../../assets/svg/filters.svg";
import Plus from "../../assets/svg/add.svg";
import ExcelImportIC from "../../assets/svg/excel-import-icon.svg";
import Dropdown from "../../assets/svg/dropdown.svg";
import Dots from "../../assets/svg/groupdots.svg";
import Customize from "../../assets/svg/customize.svg";
import lar from "../../assets/svg/lar.svg";
import rar from "../../assets/svg/rar.svg";
import Cancel from "../../assets/svg/cancel.svg";
import { ReactComponent as EditRow } from "../../assets/svg/edit.svg";
import { ReactComponent as DeleteRow } from "../../assets/svg/delete.svg";
import DeleteModal from "../common/delete-modal.js";
import exportBtn from "../../assets/svg/bl_export_ic.svg";
import uploadIcon from "../../assets/svg/upload-icon.svg";
import CommonFilter from "../common/commonfilter.js";
import ImportFileModal from "../common/importfile-modal.js";
import {
  sanitizeInput,
  sanitizeFileName,
  validateFileSize,
} from "../common/security-utils.js";
import RecordsPerPage from "../common/RecordsPerPage.jsx";
import Loader from "../common/Loader.jsx";

const ContactsList = () => {
  //Filter
  const [showFilter, setShowFilter] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const options = [
    "Contact Name",
    "Company Name",
    "Phone number",
    "Email",
    "Status",
  ];

  //Inputs
  const [leadSource, setLeadSource] = useState(null);
  const [industry, setIndustry] = useState(null);
  const [leadStatus, setLeadStatus] = useState(null);
  const [employees, setEmployees] = useState("");

  //Lead Sourse Options
  const handleLeadSourceDropdownChange = (option) => {
    setLeadSource(option.label);
  };

  const handleLeadSourceOnChange = (term) => {
    setLeadSource(term);
  };

  //Lead Status
  const handleLeadStatusDropdownChange = (option) => {
    setLeadStatus(option.label);
  };

  const handleLeadStatusOnChange = (term) => {
    setLeadStatus(term);
  };

  //Industry

  const handleIndustryDropdownChange = (option) => {
    setIndustry(option.label);
  };

  const handleIndustryOnChange = (term) => {
    setIndustry(term);
  };

  //Hovered Index
  const [hoveredIndex, setHoveredIndex] = useState(null);

  //Navigate
  const navigate = useNavigate();

  // 🔒 Permissions
  const { rolePermissionList } = useGlobal();
  const hasCreatePermission = canCreate(rolePermissionList, "Contacts");
  const hasEditPermission = canEdit(rolePermissionList, "Contacts");
  const hasDeletePermission = canDelete(rolePermissionList, "Contacts");
  const hasViewPermission = canView(rolePermissionList, "Contacts");

  //Search
  const [search, setSearch] = useState("");

  //Create Lead Dropdown
  const [open, setOpen] = useState(false);

  //Customize Table Dropdown
  const [showModal, setShowModal] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState(() =>
    Object.fromEntries(options.map((label) => [label, true])),
  );

  const toggleColumn = (label) => {
    setSelectedColumns((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };
  const anyColumnSelected = Object.values(selectedColumns).some(Boolean);
  // The header and each data row are separate flex containers (this isn't a
  // real <table>), so flexbox alone can't guarantee their columns line up —
  // each row computes its own flex distribution independently. CSS Grid with
  // an identical grid-template-columns shared by the header and every row
  // forces the same column boundaries everywhere.
  const visibleOptions = options.filter((label) => selectedColumns[label]);
  const columnsGridTemplate = visibleOptions.map(() => "1fr").join(" ") || "1fr";

  // Client-side sort — sorts only the currently loaded page of rows, no API
  // involvement. sortColumn is one of `options`; sortDirection toggles asc/desc;
  // clicking a different column resets to asc.
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  const handleSortClick = (label) => {
    if (sortColumn === label) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(label);
      setSortDirection("asc");
    }
  };

  //Delete
  const [deleteId, setDeleteId] = useState(null);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [isBulkDelete, setIsBulkDelete] = useState(false);

  useEffect(() => {
    if (deleteId != null) {
      setIsOpenDelete(true);
    }
  }, [deleteId]);

  const cancelDelete = () => {
    setIsOpenDelete(false);
    setIsBulkDelete(false);
  };

  const onSuccessDelete = (response) => {
    setDeleteId(null);
    setIsBulkDelete(false);
    setSnackbarMessage(response.message);
    setSnackbarStatus(response.success);
    handleSnackbarShow();
    if (response.success === true) {
      setIsOpenDelete(false);
      setSelectedRows([]);
      setAllSelected(false);
      getContactsList();
    }
  };

  const handleBulkDelete = () => {
    // 🔒 Permission check for delete
    if (!hasDeletePermission) {
      setSnackbarMessage("You don't have permission to delete contacts");
      setSnackbarStatus(false);
      handleSnackbarShow();
      return;
    }
    if (selectedRows.length === 0) return;
    setIsBulkDelete(true);
    setIsOpenDelete(true);
  };

  //Pagination
  const [itemOffset, setItemOffset] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [pageCount, setPageCount] = useState(0);
  const [records, setRecords] = useState(0);

  const handlePageClick = useCallback((event) => {
    const newOffset = (event.selected * itemsPerPage) % contactsList.length;
    setItemOffset(event.selected);
  }, []);

  //Get Leads List
  const [contactsList, setContactsList] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [activeFilters, setActiveFilters] = useState("");

  const getContactsList = async (filters = activeFilters) => {
    setListLoading(true);
    try {
      const response = await getContactsListApi(
        `?search=${search}&limit=${itemsPerPage}&skip=${
          itemOffset * itemsPerPage
        }${filters ?? ""}`,
      );
      if (response.data.success === true) {
        setPageCount(response.data.total_pgs);
        setRecords(response.data.total_rec);
        const formatted = formatLeadsData(response.data.contacts);
        setContactsList(formatted);
        setSelectedRows([]);
        setAllSelected(false);
      }
      return response.data;
    } catch (err) {
      // Error handled silently
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    getContactsList();
  }, [search, itemsPerPage, itemOffset]);

  // Sort only the currently loaded page — client-side, no refetch.
  const sortedContactsList = React.useMemo(() => {
    if (!sortColumn) return contactsList;
    const dir = sortDirection === "asc" ? 1 : -1;
    return [...contactsList].sort((a, b) => {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];
      if (sortColumn === "Status") {
        return (aVal === bVal ? 0 : aVal ? 1 : -1) * dir;
      }
      const aStr = (aVal ?? "").toString().toLowerCase();
      const bStr = (bVal ?? "").toString().toLowerCase();
      return aStr.localeCompare(bStr) * dir;
    });
  }, [contactsList, sortColumn, sortDirection]);

  const formatLeadsData = (rawLeads) => {
    return rawLeads.map((lead) => ({
      id: lead.id,
      "Contact Name": lead.name,
      "Company Name": lead.company_name,
      Email: lead.email,
      "Phone number": lead.phone,
      Status: lead.status,
    }));
  };

  // state
  const [selectedRows, setSelectedRows] = useState([]);
  const [allSelected, setAllSelected] = useState(false);

  // Sync allSelected state when selectedRows changes
  useEffect(() => {
    if (contactsList.length > 0) {
      setAllSelected(selectedRows.length === contactsList.length);
    }
  }, [selectedRows, contactsList]);

  // handle checkbox logic
  const toggleAll = () => {
    if (allSelected) {
      setSelectedRows([]);
    } else {
      setSelectedRows(contactsList.map((lead) => lead.id));
    }
  };

  const toggleRow = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  //Snackbar
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarStatus, setSnackbarStatus] = useState(false);
  const handleSnackbarShow = () => setShowSnackbar(true);
  const handleSnackbarClose = () => setShowSnackbar(false);

  const downloadBase64Excel = (base64String, filePrefix, fileSizeErrorMessage) => {
    if (!validateFileSize(base64String, 10)) {
      setSnackbarMessage(fileSizeErrorMessage);
      setSnackbarStatus(false);
      handleSnackbarShow();
      return false;
    }

    const fileName = sanitizeFileName(`${filePrefix}_${Date.now()}.xlsx`);
    const binary = atob(base64String);
    const array = [];

    for (let i = 0; i < binary.length; i++) {
      array.push(binary.charCodeAt(i));
    }

    const blob = new Blob([new Uint8Array(array)], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return true;
  };

  //Template Download
  const downloadTemplate = async () => {
    try {
      const response = await apiCall("GET", "contacts_template_download");
      if (response?.data?.success === true && response?.data?.base64) {
        downloadBase64Excel(
          response.data.base64,
          "Contact_Template",
          "File size exceeds 10MB limit",
        );
      }

      setSnackbarMessage(response?.data?.message || "Unable to download template");
      setSnackbarStatus(response?.data?.success === true);
      handleSnackbarShow();
      /*  setExcel(response.data.url); */
    } catch (err) {
      // Error handled silently
    } finally {
    }
  };

  const handleFileClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Security: Validate file type
      const allowedTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel"
      ];
      if (!allowedTypes.includes(file.type)) {
        setSnackbarMessage("Invalid file type. Only Excel files allowed.");
        setSnackbarStatus(false);
        handleSnackbarShow();
        return;
      }
      
      // Security: Validate file size (10MB limit)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        setSnackbarMessage("File size exceeds 10MB limit");
        setSnackbarStatus(false);
        handleSnackbarShow();
        return;
      }
      
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result;
        uploadTicket(base64String);
      };
      reader.readAsDataURL(file);
    } else {
      setSnackbarMessage("Please select a valid file.");
      setSnackbarStatus(false);
      handleSnackbarShow();
    }
  };

  const handleFilter = (filterData, type) => {
    setActiveFilters(filterData || "");
    getContactsList(filterData);
  };

  const uploadTicket = async (base64String) => {
    // Security: Validate input
    if (!base64String || typeof base64String !== "string") {
      setSnackbarMessage("Invalid file data");
      setSnackbarStatus(false);
      handleSnackbarShow();
      return;
    }
    
    setLoading(true);
    try {
      const data = {
        upload_file: base64String,
      };
      const response = await apiCall("POST", "contacts_upload", data);

      if (response?.data?.success === true) {
        // Download report first before closing modal
        if (response?.data?.base64) {
          downloadBase64Excel(
            response.data.base64,
            "Contact_Upload_Report",
            "Report file size exceeds 10MB limit",
          );
        }
        setSnackbarMessage(response.data.message);
        setSnackbarStatus(true);
        handleSnackbarShow();
        setShowImport(false);
        getContactsList();
      } else {
        setSnackbarMessage(response?.data?.message || "Upload failed");
        setSnackbarStatus(false);
        handleSnackbarShow();

        if (response?.data?.base64) {
          downloadBase64Excel(
            response.data.base64,
            "Contact_Upload_Error",
            "Error file size exceeds 10MB limit",
          );
        }
      }
    } catch (err) {
      // Error handled silently
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        fontFamily: "'Euclid Circular B', sans-serif",
        padding: "16px",
        height: "100vh",
        width: "100%",
        overflowX: "hidden",
        backgroundColor: "#f8f9fb",
      }}
    >
      <div
        style={{
          padding: "16px",
          borderRadius: "16px",
          backgroundColor: "#FFFFFF",
        }}
      >
        {isOpenDelete && (
          <DeleteModal
            deleteUrl={isBulkDelete ? null : `contact/delete/${deleteId}`}
            bulkDeleteApi={isBulkDelete ? bulkDeleteContactsApi : null}
            bulkDeleteIds={isBulkDelete ? selectedRows : null}
            onSuccess={onSuccessDelete}
            onCancel={cancelDelete}
          />
        )}
        <div style={{ marginBottom: "16px" }}>
          <div className="d-flex justify-content-between g-3">
            <div>
              {/* Contacts count */}
              <div className="fs-14 fw-600 text-dkblue">
                Contacts - {records}
              </div>
            </div>
            <div
              className="d-flex justify-content-end align-items-center"
              style={{ flexWrap: "wrap", gap: "12px" }}
            >
              {/* Search box & Filter button */}
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    borderRadius: "100px",
                    border: "0.8px solid #e5e5e5",
                    padding: "9px 16px",
                    backgroundColor: "#ffffff",
                    height: "36px",
                  }}
                >
                  <img src={Search} alt="search" style={{ width: "16px" }} />
                  <input
                    type="search"
                    placeholder="Search..."
                    className="no-focus-border"
                    value={search}
                    onChange={(e) => setSearch(sanitizeInput(e.target.value))}
                    style={{
                      border: "none",
                      outline: "none",
                      fontSize: "14px",
                      flex: 1,
                      backgroundColor: "transparent",
                    }}
                  />
                </div>
                <img
                  src={Filter}
                  alt="Filter"
                  style={{ height: "36px", width: "36px", cursor: "pointer" }}
                  onClick={() => setShowFilter(true)}
                />
              </div>

              {/* ✅ COMMON FILTER */}
              <CommonFilter
                type="contacts"
                open={showFilter}
                onClose={() => setShowFilter(false)}
                onSubmit={handleFilter}
              />

              {/* ✅ IMPORT MODAL */}
              <ImportFileModal
                type="contacts"
                open={showImport}
                onClose={() => setShowImport(false)}
                onExport={downloadTemplate}
                onImport={(base64) => uploadTicket(base64)}
              />

              {/* Create/Delete buttons */}
              <div className="d-flex justify-content-end align-items-center">
                {selectedRows.length === 0 && hasCreatePermission && (
                  <div
                    style={{ position: "relative", display: "inline-block" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        padding: "8px 16px",
                        borderRadius: "100px",
                        borderBottom: "3px solid #1723C2",
                        background:
                          "linear-gradient(180deg, #6971DD 0%, #3D4AF5 100%)",
                        height: "40px",
                        width: "fit-content",
                        cursor: "pointer",
                      }}
                      onClick={() => navigate("/contacts/create")}
                    >
                      <img src={Plus} style={{ width: "16px" }} alt="Plus" />
                      <div className="fs-15 fw-600 text-white">Create</div>
                    </div>
                  </div>
                )}
                {selectedRows.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "10px",
                      padding: "0 20px",
                      borderRadius: "50px",
                      borderBottom: "3px solid #B31717",
                      background:
                        "linear-gradient(180deg, #FF7A7A 0%, #D61916 100%)",
                      height: "40px",
                      width: "auto",
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                    }}
                    onClick={handleBulkDelete}
                  >
                    <div className="fs-15 fw-600 text-white">Delete ({selectedRows.length})</div>
                  </div>
                )}
                <div
                  style={{
                    position: "relative",
                    display: "inline-block",
                    marginLeft: "10px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      padding: "8px 16px",
                      borderRadius: "100px",
                      border: "1px solid #3D4AF5",
                      height: "36px",
                      width: "fit-content",
                      cursor: "pointer",
                    }}
                    onClick={() => setShowImport(true)}
                  >
                    <img
                      src={ExcelImportIC}
                      style={{ width: "16px" }}
                      alt="Import"
                    />
                    <div
                      style={{
                        fontWeight: "600",
                        fontSize: "16px",
                        color: "#3D4AF5",
                      }}
                    >
                      Import
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginBottom: "16px" }}>
          <Row>
            <Col sm={12}>
              <div
                style={{
                  borderRadius: "16px",
                  border: "1px solid #ebebeb",
                  backgroundColor: "#ffffff",
                  overflow: "hidden",
                }}
                className="table-scroll-container"
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                {/* Header */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    borderBottom: "1px solid #ebebeb",
                  }}
                >
                  <div
                    style={{
                      width: "72px",
                      padding: "10px 24px",
                      flexShrink: 0,
                      borderRight: "1px solid #ebebeb",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                    />
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: columnsGridTemplate, flex: 1 }}>
                    {visibleOptions.map((label) => (
                      <div
                        key={label}
                        onClick={() => handleSortClick(label)}
                        style={{
                          minWidth: 0,
                          padding: "10px 16px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          borderRight: "1px solid #ebebeb",
                          cursor: "pointer",
                          userSelect: "none",
                        }}
                        title={`Sort by ${label}`}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            minWidth: 0,
                          }}
                        >
                          <div
                            className="fs-12 fw-600 text-ltgrey"
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {label.toUpperCase()}
                          </div>
                          <span
                            className="fs-12 fw-600"
                            style={{
                              flexShrink: 0,
                              color: sortColumn === label ? "#3D4AF5" : "#c4c9d4",
                            }}
                          >
                            {sortColumn === label && sortDirection === "desc" ? "▼" : "▲"}
                          </span>
                        </div>
                        <img src={Dots} alt="dots" style={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()} />
                      </div>
                    ))}
                  </div>

                  <div
                    onClick={() => setShowModal(true)}
                    style={{
                      width: "72px",
                      flexShrink: 0,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {/* Customize Icon (Header) */}
                    <img src={Customize} alt="customize" />
                  </div>
                </div>

                {/* Data Rows */}
                {listLoading ? (
                  <div style={{ width: "100%", padding: "40px 0" }}>
                    <Loader height="120px" />
                  </div>
                ) : sortedContactsList.length === 0 ? (
                  <div style={{ width: "100%", textAlign: "center", padding: "40px 0", color: "#8790A1" }}>
                    No contacts found
                  </div>
                ) : (
                  sortedContactsList.map((contact, rowIndex) => (
                  <div
                    key={contact.id}
                    onMouseEnter={() => setHoveredIndex(rowIndex)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    style={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      borderBottom: "1px solid #ebebeb",
                      backgroundColor: "#ffffff",
                      cursor: "pointer",
                    }}
                  >
                    <div
                      style={{
                        width: "72px",
                        padding: "10px 24px",
                        flexShrink: 0,
                        borderRight: "1px solid #ebebeb",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(contact.id)}
                        onChange={() => toggleRow(contact.id)}
                        disabled={!anyColumnSelected}
                      />
                    </div>
                    <div
                      style={{ display: "grid", gridTemplateColumns: columnsGridTemplate, flex: 1, cursor: hasViewPermission ? "pointer" : "not-allowed" }}
                      onClick={() => {
                        if (hasViewPermission) {
                          navigate("/contacts/view", {
                            state: { id: contact.id },
                          });
                        }
                      }}
                    >
                      {visibleOptions.map((label) => (
                        <div
                          key={label}
                          title={label === "Status" ? (contact[label] ? "Active" : "Inactive") : contact[label]}
                          className="text-dkblue fs-14 fw-400"
                          style={{
                            minWidth: 0,
                            padding: "10px 16px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            borderRight: "1px solid #ebebeb",
                            display: label === "Status" ? "flex" : "block",
                            alignItems: label === "Status" ? "center" : "initial",
                          }}
                        >
                          {label === "Status" ? (
                            <div
                              style={{
                                borderRadius: "8px",
                                backgroundColor: contact[label] ? "#d9ffe9" : "#FDF0F0",
                                height: "24px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "0 8px",
                                width: "fit-content",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  color: contact[label] ? "#2d9d78" : "#d61916",
                                }}
                              >
                                {contact[label] ? "Active" : "Inactive"}
                              </span>
                            </div>
                          ) : (
                            contact[label] || "-"
                          )}
                        </div>
                      ))}
                    </div>
                    <div style={{ width: "72px", flexShrink: 0 }} />
                    {hoveredIndex === rowIndex && (
                      <div
                        style={{
                          position: "absolute",
                          top: "50%",
                          right: "12px",
                          transform: "translateY(-50%)",
                          zIndex: 1,
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center" }}>
                          {hasEditPermission && (
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate("/contacts/create", {
                                  state: { id: contact.id },
                                });
                              }}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                border: "0.6px solid #E5E5E5",
                                cursor: "pointer",
                                justifyContent: "center",
                                width: "32px",
                                height: "32px",
                                borderRadius: "100px",
                                background: "#FFFFFF",
                              }}
                              title="Edit Contact"
                            >
                              <EditRow />
                            </div>
                          )}
                          {hasEditPermission && hasDeletePermission && <span style={{ marginLeft: "16px" }}></span>}
                          {hasDeletePermission && (
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteId(contact.id);
                              }}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                border: "0.6px solid #E5E5E5",
                                cursor: "pointer",
                                justifyContent: "center",
                                width: "32px",
                                height: "32px",
                                borderRadius: "100px",
                                background: "#FFFFFF",
                              }}
                              title="Delete Contact"
                            >
                              <DeleteRow />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  ))
                )}

                {showModal && (
                  <div
                    style={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      width: "100vw",
                      height: "100vh",
                      backgroundColor: "rgba(0,0,0,0.5)",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      zIndex: 1000,
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: "#fff",
                        padding: "24px",
                        borderRadius: "12px",
                        boxShadow: "0px 4px 12px rgba(0,0,0,0.2)",
                        width: "300px",
                        maxHeight: "80vh",
                        overflowY: "auto",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: "600",
                          fontSize: "16px",
                          marginBottom: "16px",
                        }}
                      >
                        Select Columns
                      </div>
                      {options.map((label, index) => (
                        <label
                          key={index}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            marginBottom: "10px",
                            fontSize: "14px",
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={!!selectedColumns[label]}
                            onChange={() => toggleColumn(label)}
                            style={{ marginRight: "8px" }}
                          />
                          {label}
                        </label>
                      ))}
                      <button
                        onClick={() => setShowModal(false)}
                        style={{
                          marginTop: "16px",
                          width: "100%",
                          padding: "10px",
                          borderRadius: "8px",
                          border: "none",
                          backgroundColor: "#3D4AF5",
                          color: "#fff",
                          fontWeight: "500",
                          cursor: "pointer",
                        }}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}
                </div>
              </div>
            </Col>
          </Row>
        </div>

        <div>
          <Row>
            <Col>
              <RecordsPerPage
                value={itemsPerPage}
                onChange={(event) => {
                  setItemsPerPage(event.target.value);
                }}
              />
            </Col>
            <Col>
              <div className="d-flex justify-content-end">
                <ReactPaginate
                  previousLabel={
                    <img src={lar} className="ar3" alt="previous" />
                  }
                  nextLabel={<img src={rar} className="ar3" alt="next" />}
                  breakLabel={"..."}
                  pageCount={pageCount + 1}
                  marginPagesDisplayed={2}
                  pageRangeDisplayed={3}
                  onPageChange={handlePageClick}
                  containerClassName={"pagination"}
                  activeClassName={"active"}
                />
              </div>
            </Col>
          </Row>

          <ToastContainer position="top-end">
            <Toast
              className="d-inline-block m-1"
              bg={snackbarStatus === true ? "success" : "danger"}
              show={showSnackbar}
              onClose={handleSnackbarClose}
              delay={3000}
              autohide
            >
              <Toast.Body style={{ color: "#ffffff" }}>
                {snackbarMessage}
              </Toast.Body>
            </Toast>
          </ToastContainer>
        </div>
      </div>
    </div>
  );
};

export default ContactsList;
